import ScrollTo from 'animated-scroll-to';
import cn from 'classnames';
import { useLayoutEffect, useMemo, useRef } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { SubstrateType } from '@webapp/common/lib/ui';

import { substrateStyles } from '@webapp/survey/src/components/questions/lib';
import { getQ } from '@webapp/survey/src/machine/lib';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';
import { selectInfo, selectSurveyPages } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { scrollToTop } from '@webapp/survey/src/utils/scroll';

import { QuestionView } from './components';

import css from './questions.css';

const NoQuestions: FC = () => <div>Нет вопросов</div>;

export const QuestionsSection: VFC<{
    questions: Array<QuestionActorRef>;
    scrollToQuestionId?: number;
    resetScroll?: () => void;
    readOnly?: boolean;
    twisterReadOnly?: boolean;
    showHeader?: boolean;
    offset?: number;
}> = ({ questions, readOnly, twisterReadOnly, resetScroll, scrollToQuestionId, showHeader, offset }) => {
    const pages = useStateSelector(selectSurveyPages);
    const { design } = useStateSelector(selectInfo);
    const firstInvalidRef = useRef<HTMLDivElement>();
    const { questionGap } = useTextStyle();
    const style = useMemo(() => {
        let styles: CSSProperties = {
            rowGap: questionGap,
            marginBottom: questionGap,
        };

        if (design.brand.substrate === SubstrateType.FULL_SURVEY) {
            styles = { ...styles, ...substrateStyles };
        }

        return styles;
    }, [questionGap, design.brand.substrate]);

    const questionsElements = useMemo(
        () =>
            questions.map((q) => {
                const scrollToSelf = scrollToQuestionId === getQ(q).id;
                const ref = scrollToSelf ? firstInvalidRef : null;
                const page = pages.find((p) => p.id === getQ(q).page);

                return (
                    <QuestionView
                        key={q.id}
                        page={page}
                        question={q}
                        readOnly={readOnly}
                        twisterReadOnly={twisterReadOnly}
                        ref={ref}
                        showHeader={showHeader}
                        substrateStyles={substrateStyles}
                        offset={offset}
                    />
                );
            }),
        [questions, readOnly, twisterReadOnly, scrollToQuestionId, showHeader, pages],
    );

    useLayoutEffect(() => {
        let timer;
        if (scrollToQuestionId) {
            timer = setTimeout(() => {
                const { current } = firstInvalidRef;
                if (current) {
                    parent && parent.postMessage(`scrollTo::${JSON.stringify(current.getBoundingClientRect())}`, '*');
                    void ScrollTo(current, {
                        speed: 1000,
                    }).then(() => {
                        resetScroll && resetScroll();
                    });
                }
            }, 500);
        }
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollToQuestionId, firstInvalidRef.current]);

    useLayoutEffect(() => {
        scrollToTop();
    }, []);

    return (
        <div className={cn(css.questions, 'questions')} style={style}>
            {questions.length > 0 ? questionsElements : <NoQuestions />}
        </div>
    );
};
QuestionsSection.displayName = 'QuestionsSection';
